<template>
  <div id="app">
    <router-view v-slot="{ Component, route }">
      <template v-if="route.path.startsWith('/app/')">
        <transition :name="(route.meta.transition as string) || ''">
          <div class="route-wrapper" :key="route.path">
            <component :is="Component" />

            <Teleport to="body">
              <component
                v-for="popup in popupStore.popups"
                :key="popup.id"
                :is="popup.component"
                :item="popup"
                :style="{ zIndex: 1000 + popupStore.popups.indexOf(popup) }"
              />
            </Teleport>
          </div>
        </transition>
      </template>

      <template v-else>
        <component :is="Component" />

        <Teleport to="body">
          <component
            v-for="popup in popupStore.popups"
            :key="popup.id"
            :is="popup.component"
            :item="popup"
            :style="{ zIndex: 1000 + popupStore.popups.indexOf(popup) }"
          />
        </Teleport>
      </template>
    </router-view>

    <CommonAlertModal />
    <ApecAlertModal />
    <CallModal />
    <ModalRoot />

    <ApecToast v-if="themeStore.theme === 'apec'" />
    <Toast v-else />
  </div>
</template>

<script setup lang="ts">
import CallModal from '@/components/domains/admin/common/CallModal.vue';
import ApecToast from '@/components/domains/apec/common/ApecToast.vue';
import ApecAlertModal from '@/components/shared/composite/ApecModalAlert.vue';
import CommonAlertModal from '@/components/shared/composite/ModalCommonAlert.vue';
import ModalRoot from '@/components/shared/composite/ModalRoot.vue';
import Toast from '@/components/shared/composite/Toast.vue';
import { useButtonBackAndroid } from '@/composables/domains/mobile/useButtonBackAndroid';
import { usePopupStore } from '@/stores/popup';
import { useSocketStore } from '@/stores/socket';
import { useThemeStore } from '@/stores/theme';
import { useToastStore } from '@/stores/toast';

const router = useRouter();

const popupStore = usePopupStore();
const socketStore = useSocketStore();
const toastStore = useToastStore();
const themeStore = useThemeStore();

onMounted(() => {
  themeStore.updateTheme('light');
});

useButtonBackAndroid(router, {
  topLevelRoutes: ['app-main-view', 'native-login'],
  interceptors: [
    () => {
      return false;
    },
  ],
  onTopLevelBackHint: () => toastStore.info('한 번 더 누르면 종료됩니다'),
  perRoute: (name) => {
    if (name === 'chat-entrance') {
      router.replace({ name: 'app-main-view' });
      return true;
    }
    if (popupStore.popups.length > 0) {
      popupStore.closeLast();
      return true;
    }
    // 셀프서비스 채팅방에서 소켓이 연결 중일 때, canGoBack 여부와 관계없이
    // router.back()으로 beforeEach 가드(handleGuardExit)를 통해 종료 확인 모달을 표시합니다.
    if (router.currentRoute.value.meta?.chatType && socketStore.socketState !== 'CLOSED') {
      router.back();
      return true;
    }
    return false;
  },
});
</script>

<style lang="scss">
.route-wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100% !important;
  height: 100vh;
  height: 100dvh;
  background-color: #ffffff;
  overflow: hidden;
  isolation: isolate;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}
.route-wrapper > div {
  -webkit-overflow-scrolling: touch; /* iOS 부드러운 스크롤 */
  will-change: transform; /* GPU 가속 미리 준비 */
}

// .slide-left-enter-active {
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   z-index: 999;
//   background-color: #ffffff;
//   transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
//   will-change: transform;
// }
// .slide-left-enter-from {
//   transform: translateX(100%);
// }
// .slide-left-enter-to {
//   transform: translateX(0);
// }

// .slide-left-leave-active {
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   z-index: 999;
//   background-color: #ffffff;
//   transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
//   will-change: transform;
// }
// .slide-left-leave-from {
//   transform: translateX(0);
// }
// .slide-left-leave-to {
//   transform: translateX(-100%);
// } /* 왼쪽으로 나감 */

.slide-left-enter-active {
  z-index: 999;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: transform;
}

.slide-left-enter-from {
  transform: translate3d(100%, 0, 0);
  -webkit-transform: translate3d(100%, 0, 0);
}
.slide-left-enter-to {
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  backface-visibility: hidden; /* 뒷면 숨김 */
  -webkit-backface-visibility: hidden;
  -webkit-perspective: 1000px;
  perspective: 1000px; /* 하드웨어 가속 유도 */
  will-change: transform;
}
.slide-left-leave-active::after {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 100;
}

.slide-left-leave-from {
  transform: translate3d(0, 0, 0);
}

.slide-left-leave-to {
  transform: translate3d(-0.1%, 0, 0);
}
.slide-left-leave-to::after {
  opacity: 1;
}

/* 1. 새로 들어오는 페이지 (이전 페이지가 다시 보임) */
.slide-right-enter-active {
  z-index: 1; /* 현재 페이지보다 아래에 위치 */
  background-color: #ffffff;
  transition: none;
}

/* 2. 나가는 페이지 (현재 보고 있는 페이지가 오른쪽으로 빠짐) */
.slide-right-leave-active {
  z-index: 999;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-right-leave-from {
  transform: translate3d(0, 0, 0);
  -webkit-transform: translate3d(0, 0, 0);
}

.slide-right-leave-to {
  -webkit-transform: translate3d(100%, 0, 0);
  transform: translate3d(100%, 0, 0); /* 오른쪽으로 완전히 밀어냄 */
}

// // 뒤로가기: 현재 페이지가 오른쪽으로 나가고, 이전 페이지는 왼쪽에서 들어옴 */
// .slide-right-enter-active {
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   z-index: 999;
//   background-color: #ffffff;
//   transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
//   will-change: transform;
// }
// .slide-right-enter-from {
//   transform: translateX(-100%);
// }
// .slide-right-enter-to {
//   transform: translateX(0);
// }

// .slide-right-leave-active {
//   position: absolute;
//   top: 0;
//   left: 0;
//   width: 100%;
//   height: 100%;
//   z-index: 999;
//   background-color: #ffffff;
//   transition: transform 1s cubic-bezier(0.4, 0, 0.2, 1);
//   will-change: transform;
// }
// .slide-right-leave-from {
//   transform: translateX(0);
// }
// .slide-right-leave-to {
//   transform: translateX(100%);
// } /* 오른쪽으로 나감 */
</style>
